import { CdkDrag } from '@angular/cdk/drag-drop';
import { OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, CdkPortalOutlet } from '@angular/cdk/portal';
import { AfterViewInit, Component, ComponentRef, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { ResolvedFloatingDialogConfig } from '../../service/floating-dialog/floating-dialog-config';
import { FloatingDialogRef } from '../../service/floating-dialog/floating-dialog-ref';

const openDialogHosts = new Set<HTMLElement>();

export function bringFloatingDialogToFront(hostElement: HTMLElement): void {
  const parent = hostElement.parentElement;
  if (!parent) return;
  let anotherDialogAbove = false;
  openDialogHosts.forEach(other => {
    // eslint-disable-next-line no-bitwise -- compareDocumentPosition returns a bitmask
    if (other !== hostElement && (hostElement.compareDocumentPosition(other) & Node.DOCUMENT_POSITION_FOLLOWING))
      anotherDialogAbove = true;
  });
  if (anotherDialogAbove)
    parent.appendChild(hostElement);
}

@Component({
  selector: 'app-floating-dialog',
  templateUrl: './floating-dialog.component.html',
  styleUrls: ['./floating-dialog.component.scss']
})
export class FloatingDialogComponent implements AfterViewInit, OnDestroy {

  @ViewChild(CdkPortalOutlet, { static: true }) portalOutlet: CdkPortalOutlet;
  @ViewChild('panel', { static: true }) panel: ElementRef<HTMLElement>;
  @ViewChild(CdkDrag, { static: true }) drag: CdkDrag;

  isMaximize = false;

  modal = true;
  closable = true;
  maximizable = true;
  resizable = true;

  private width: string | undefined;
  private height: string | undefined;
  private minWidth: number | string | undefined;
  private minHeight: number | string | undefined;
  private maxWidth: number | string | undefined;
  private maxHeight: number | string | undefined;
  private fitContent = false;

  dialogStyle: Record<string, string> = this.computeDialogStyle();

  private manualWidth: number | null = null;
  private manualHeight: number | null = null;
  private paneStyleBeforePinning: { position: string; left: string; top: string } | null = null;

  constructor(private dialogRef: FloatingDialogRef, private overlayRef: OverlayRef, private ngZone: NgZone) {
  }

  ngAfterViewInit(): void {

    openDialogHosts.add(this.overlayRef.hostElement);

    this.ngZone.runOutsideAngular(() => {
      this.overlayRef.overlayElement.addEventListener(
        'mousedown', () => setTimeout(() => bringFloatingDialogToFront(this.overlayRef.hostElement)), { capture: true });
    });
  }

  ngOnDestroy(): void {
    openDialogHosts.delete(this.overlayRef.hostElement);
  }

  applyConfig(config: ResolvedFloatingDialogConfig): void {
    this.modal = config.modal;
    this.closable = config.closable;
    this.maximizable = config.maximizable;
    this.resizable = config.resizable;
    this.width = config.width;
    this.height = config.height;
    this.minWidth = config.minWidth;
    this.minHeight = config.minHeight;
    this.maxWidth = config.maxWidth;
    this.maxHeight = config.maxHeight;
    this.fitContent = config.fitContent;
    this.dialogStyle = this.computeDialogStyle();
  }


  attachContent<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    return this.portalOutlet.attachComponentPortal(portal);
  }

  close(): void {
    this.dialogRef.close();
  }

  onMaximizeToggle(): void {
    this.isMaximize = !this.isMaximize;

    this.unpinPane();
    this.drag._dragRef.reset();
    this.dialogStyle = this.computeDialogStyle();
  }

  // Pane jest domyślnie centrowany, więc zmiana rozmiaru rozjeżdżałaby się w obie strony od środka.
  // Przypięcie do bieżącej pozycji sprawia, że resize przesuwa tylko prawą i dolną krawędź.
  private pinPane(): void {
    if (this.paneStyleBeforePinning) return;
    const pane = this.overlayRef.overlayElement;
    const paneRect = pane.getBoundingClientRect();
    const dragOffset = this.drag.getFreeDragPosition();
    this.paneStyleBeforePinning = { position: pane.style.position, left: pane.style.left, top: pane.style.top };
    pane.style.position = 'absolute';
    pane.style.left = `${paneRect.left - dragOffset.x}px`;
    pane.style.top = `${paneRect.top - dragOffset.y}px`;
  }

  private unpinPane(): void {
    if (!this.paneStyleBeforePinning) return;
    const pane = this.overlayRef.overlayElement;
    pane.style.position = this.paneStyleBeforePinning.position;
    pane.style.left = this.paneStyleBeforePinning.left;
    pane.style.top = this.paneStyleBeforePinning.top;
    this.paneStyleBeforePinning = null;
  }

  private computeDialogStyle(): Record<string, string> {
    const style: Record<string, string> = {
      display: 'flex',
      'flex-direction': 'column',
      width: this.isMaximize ? '100vw' : (this.manualWidth != null ? `${this.manualWidth}px` : (this.width ?? (this.fitContent ? 'auto' : '55vw'))),
      height: this.isMaximize ? '100vh' : (this.manualHeight != null ? `${this.manualHeight}px` : (this.height ?? (this.fitContent ? 'auto' : '65vh')))
    };
    if (!this.isMaximize) {
      this.putCssSize(style, 'min-width', this.minWidth);
      this.putCssSize(style, 'min-height', this.minHeight);
      this.putCssSize(style, 'max-width', this.maxWidth);
      this.putCssSize(style, 'max-height', this.maxHeight);
    }
    return style;
  }

  private putCssSize(style: Record<string, string>, property: string, value: number | string): void {
    if (value != null)
      style[property] = typeof value === 'number' ? `${value}px` : value;
  }

  startResize(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const container = this.panel.nativeElement;

    const startX = event.pageX;
    const startY = event.pageY;
    const rect = container.getBoundingClientRect();
    const startWidth = rect.width;
    const startHeight = rect.height;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    this.pinPane();

    this.ngZone.runOutsideAngular(() => {
      let pendingEvent: MouseEvent | null = null;
      let frameScheduled = false;

      const applyPendingResize = (): void => {
        frameScheduled = false;
        if (!pendingEvent) return;
        container.style.width = `${Math.max(300, startWidth + (pendingEvent.pageX - startX))}px`;
        container.style.height = `${Math.max(200, startHeight + (pendingEvent.pageY - startY))}px`;
      };

      const onMouseMove = (e: MouseEvent): void => {
        pendingEvent = e;
        if (!frameScheduled) {
          frameScheduled = true;
          requestAnimationFrame(applyPendingResize);
        }
      };
      const onMouseUp = (): void => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.userSelect = previousUserSelect;
        const finalRect = container.getBoundingClientRect();
        this.ngZone.run(() => {
          this.manualWidth = finalRect.width;
          this.manualHeight = finalRect.height;
          this.dialogStyle = this.computeDialogStyle();
        });
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  }
}
